import { useEffect, useRef, useState } from "react";
import { AutoComplete } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import agent from "../../agent";

const DAYS = ["Пн.", "Вт.", "Ср.", "Чт.", "Пт.", "Сб.", "Вс."];
// 7 дней недели и восьмая строка «для всех дней»
const emptyTime = () => Array.from({ length: 8 }, () => [null, null]);

const emptyTerminal = () => ({
  city: null,
  street: "",
  house: "",
  office: "",
  streetType: null,
  cargo: null,
  ptcDelivery: true,
  pickup: true,
  delivery: true,
  doorPickup: true,
  doorDelivery: true,
  name: "",
  lastName: "",
  surname: "",
  phone: null,
  time: emptyTime(),
  comment: "",
  codeDPD: "",
});

const requiredFields = ["city", "street", "house", "streetType", "cargo"];

const toDate = (hhmm) => (hhmm ? new Date(`December 1, 2023 ${hhmm}:00`) : null);
const toHhmm = (date) => (date ? date.toLocaleTimeString("ru").slice(0, 5) : null);

/**
 * Форма терминала перевозчика в opostdb. Поля и контракт те же, что у
 * прежней формы «Терминалы» (до сценария «терминал / кросс-докинг»).
 */
const DialogEditCarrierTerminal = ({
  editTerminal,
  cities,
  streetAbbrOptions,
  postalServices,
  showMessage,
  onSuccess,
  onHide,
}) => {
  const [terminal, setTerminal] = useState(emptyTerminal);
  const [cityOptions, setCityOptions] = useState([]);
  const [requireds, setRequireds] = useState([]);
  const [saving, setSaving] = useState(false);
  const searchTimer = useRef(null);

  // DPD и SLQ — по коду перевозчика: у каждого в opostdb несколько продуктов
  const carrierCode = postalServices?.find((ps) => ps?.id === terminal.cargo)?.code;
  const isDpd = carrierCode === "DPD";
  const isSlq = carrierCode === "SLQ";

  useEffect(() => {
    if (!editTerminal) return;
    setTerminal({
      ...emptyTerminal(),
      comment: editTerminal.message || "",
      city: cities?.find((c) => c?.kato === editTerminal.kato) || { name: editTerminal.kato, kato: editTerminal.kato },
      street: editTerminal.street || "",
      house: editTerminal.house || "",
      office: editTerminal.office || "",
      streetType: editTerminal.street_abbr_id || null,
      cargo: editTerminal.postal_service_id || null,
      ptcDelivery: editTerminal.is_term_ptc,
      pickup: editTerminal.pickup ?? true,
      delivery: editTerminal.delivery ?? true,
      doorPickup: editTerminal.doorPickup ?? true,
      doorDelivery: editTerminal.doorDelivery ?? true,
      name: editTerminal.dispatcher?.firstName || "",
      lastName: editTerminal.dispatcher?.lastName || "",
      surname: editTerminal.dispatcher?.middleName || "",
      phone: +editTerminal.dispatcher?.phone || null,
      codeDPD: editTerminal.codeDPD || "",
      time: [
        ...Array.from({ length: 7 }, (_, index) => {
          const day = (editTerminal.timeJson || []).find((d) => d?.day === index + 1);
          return [toDate(day?.start), toDate(day?.end)];
        }),
        [null, null],
      ],
    });
  }, [editTerminal, cities]);

  const handleField = (key, value) => {
    setTerminal((prev) => ({ ...prev, [key]: value }));
    if (value && requireds.includes(key)) {
      setRequireds(requireds.filter((field) => field !== key));
    }
  };

  const searchCity = (event) => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setCityOptions((await agent.NsiTerminals.searchCity(event.query)) || []);
    }, 250);
  };

  const handleTime = (type, index, value) => {
    setTerminal((prev) => {
      const time = prev.time.map((day) => [...day]);
      if (type === "clear") {
        time[index] = [null, null];
      } else {
        const slot = type === "start" ? 0 : 1;
        // восьмая строка ставит время сразу всем дням
        (index === 7 ? time : [time[index]]).forEach((day) => { day[slot] = value; });
      }
      return { ...prev, time };
    });
  };

  const invalid = (field) => (requireds.includes(field) ? "p-invalid" : "");

  const handleSave = async () => {
    const missing = [
      ...requiredFields,
      ...(isDpd ? ["codeDPD"] : []),
      ...(isSlq ? ["name", "lastName", "phone"] : []),
    ].filter((field) => !terminal[field]);
    if (missing.length) {
      setRequireds(missing);
      showMessage("error", "Ошибка", "Заполните все обязательные поля");
      return;
    }

    const hasDispatcher = terminal.name || terminal.lastName || terminal.surname || terminal.phone;
    const data = {
      street: terminal.street,
      house: terminal.house,
      office: terminal.office,
      kato: terminal.city?.kato || "",
      street_abbr_id: terminal.streetType,
      postal_service_id: terminal.cargo,
      is_term_ptc: terminal.ptcDelivery,
      pickup: terminal.pickup,
      delivery: terminal.delivery,
      doorPickup: terminal.doorPickup,
      doorDelivery: terminal.doorDelivery,
      message: terminal.comment,
      codeDPD: terminal.codeDPD,
      timeJson: terminal.time.slice(0, 7).map((t, index) => ({
        day: index + 1,
        weekend: index >= 5,
        start: toHhmm(t[0]),
        end: toHhmm(t[1]),
      })),
      dispatcher: hasDispatcher
        ? { firstName: terminal.name, lastName: terminal.lastName, middleName: terminal.surname, phone: terminal.phone }
        : null,
    };

    setSaving(true);
    try {
      if (editTerminal) {
        await agent.NsiTerminals.updateTerminal(editTerminal.id, data);
        showMessage("success", "Редактирование терминала", "Терминал изменён");
      } else {
        await agent.NsiTerminals.addTerminal(data);
        showMessage("success", "Создание терминала", "Терминал создан");
      }
      onSuccess();
    } catch (err) {
      showMessage("error", `Терминал не ${editTerminal ? "изменён" : "добавлен"}`, agent.handleError(err));
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <div>
      <Button label="Отменить" icon="pi pi-times" onClick={onHide} className="p-button-text" />
      <Button label="Сохранить" icon="pi pi-check" onClick={handleSave} loading={saving} autoFocus />
    </div>
  );

  const timeRow = (index, label) => (
    <div key={index} className="p-d-flex p-ai-center p-mb-2" style={{ gap: 8 }}>
      <b style={{ width: "3rem" }}>{label}</b>
      <Calendar value={terminal.time[index][0]} onChange={(e) => handleTime("start", index, e.value)} timeOnly style={{ width: "6rem" }} />
      <span>—</span>
      <Calendar value={terminal.time[index][1]} onChange={(e) => handleTime("end", index, e.value)} timeOnly style={{ width: "6rem" }} />
      {index < 7 && (
        <Button onClick={() => handleTime("clear", index)} className="p-button-text p-button-rounded" icon="pi pi-times" tooltip="Выходной" />
      )}
    </div>
  );

  return (
    <Dialog
      header={editTerminal ? "Редактирование терминала" : "Добавление терминала"}
      visible
      style={{ width: "60vw" }}
      breakpoints={{ "960px": "90vw" }}
      footer={footer}
      onHide={onHide}
    >
      <div className="p-fluid p-formgrid p-grid">
        <div className="p-field p-col-12 p-md-4">
          <label htmlFor="city">Город</label>
          <AutoComplete
            id="city"
            className={`p-mt-1 ${invalid("city")}`}
            value={terminal.city}
            suggestions={cityOptions}
            completeMethod={searchCity}
            field="name"
            dropdown
            onChange={(e) => handleField("city", e.value)}
          />
        </div>
        <div className="p-field p-col-12 p-md-4">
          <label htmlFor="streetAbbr">Тип улицы</label>
          <Dropdown
            inputId="streetAbbr"
            className={`p-mt-1 ${invalid("streetType")}`}
            value={terminal.streetType}
            options={streetAbbrOptions}
            onChange={(e) => handleField("streetType", e.value)}
            optionLabel="nameRu"
            optionValue="id"
            showClear
          />
        </div>
        <div className="p-field p-col-12 p-md-4">
          <label htmlFor="street">Улица</label>
          <InputText id="street" className={`p-mt-1 ${invalid("street")}`} value={terminal.street}
                     onChange={(e) => handleField("street", e.target.value)} />
        </div>
        <div className="p-field p-col-12 p-md-4">
          <label htmlFor="house">Дом</label>
          <InputText id="house" className={`p-mt-1 ${invalid("house")}`} value={terminal.house}
                     onChange={(e) => handleField("house", e.target.value)} />
        </div>
        <div className="p-field p-col-12 p-md-4">
          <label htmlFor="office">Офис</label>
          <InputText id="office" className="p-mt-1" value={terminal.office}
                     onChange={(e) => handleField("office", e.target.value)} />
        </div>
        <div className="p-field p-col-12 p-md-4">
          <label htmlFor="psId">Перевозчик</label>
          <Dropdown
            inputId="psId"
            className={`p-mt-1 ${invalid("cargo")}`}
            value={terminal.cargo}
            options={postalServices}
            onChange={(e) => handleField("cargo", e.value)}
            optionLabel="label"
            optionValue="id"
            filter
            showClear
          />
        </div>

        <div className="p-field p-col-12 p-d-flex p-ai-center" style={{ gap: 24 }}>
          {/* В базе флаги «можно»: pickup — терминал принимает груз, delivery — выдаёт */}
          <div className="p-d-flex p-ai-center" style={{ gap: 6 }}>
            <Checkbox inputId="pickup" checked={terminal.pickup} onChange={(e) => handleField("pickup", e.checked)} />
            <label htmlFor="pickup">Принимает груз от отправителя</label>
          </div>
          <div className="p-d-flex p-ai-center" style={{ gap: 6 }}>
            <Checkbox inputId="delivery" checked={terminal.delivery} onChange={(e) => handleField("delivery", e.checked)} />
            <label htmlFor="delivery">Выдаёт груз получателю</label>
          </div>
        </div>

        <div className="p-field p-col-12 p-d-flex p-ai-center" style={{ gap: 24 }}>
          {/* Работа перевозчика по городу — общая для всех его терминалов этого продукта в пункте */}
          <div className="p-d-flex p-ai-center" style={{ gap: 6 }}>
            <Checkbox inputId="doorPickup" checked={terminal.doorPickup} onChange={(e) => handleField("doorPickup", e.checked)} />
            <label htmlFor="doorPickup">Забор от двери по городу</label>
          </div>
          <div className="p-d-flex p-ai-center" style={{ gap: 6 }}>
            <Checkbox inputId="doorDelivery" checked={terminal.doorDelivery} onChange={(e) => handleField("doorDelivery", e.checked)} />
            <label htmlFor="doorDelivery">Доставка до двери по городу</label>
          </div>
        </div>

        {isDpd && (
          <div className="p-field p-col-12 p-md-4">
            <label htmlFor="codeDPD">Код терминала DPD</label>
            <InputText id="codeDPD" className={`p-mt-1 ${invalid("codeDPD")}`} value={terminal.codeDPD}
                       onChange={(e) => handleField("codeDPD", e.target.value)} />
          </div>
        )}
      </div>

      {isSlq && (
        <>
          <h4>Диспетчер терминала SLQ</h4>
          <div className="p-fluid p-formgrid p-grid">
            <div className="p-field p-col-12 p-md-3">
              <label htmlFor="name">Имя</label>
              <InputText id="name" className={`p-mt-1 ${invalid("name")}`} value={terminal.name}
                         onChange={(e) => handleField("name", e.target.value)} />
            </div>
            <div className="p-field p-col-12 p-md-3">
              <label htmlFor="lastName">Фамилия</label>
              <InputText id="lastName" className={`p-mt-1 ${invalid("lastName")}`} value={terminal.lastName}
                         onChange={(e) => handleField("lastName", e.target.value)} />
            </div>
            <div className="p-field p-col-12 p-md-3">
              <label htmlFor="surname">Отчество</label>
              <InputText id="surname" className="p-mt-1" value={terminal.surname}
                         onChange={(e) => handleField("surname", e.target.value)} />
            </div>
            <div className="p-field p-col-12 p-md-3">
              <label htmlFor="phone">Телефон</label>
              <InputNumber inputId="phone" className={`p-mt-1 ${invalid("phone")}`} value={terminal.phone}
                           onValueChange={(e) => handleField("phone", e.value)} mode="decimal" useGrouping={false} />
            </div>
          </div>
        </>
      )}

      <h4>Время работы</h4>
      <div className="p-mb-2">{timeRow(7, "Все:")}</div>
      <div className="p-grid">
        <div className="p-col-12 p-md-6">{DAYS.slice(0, 4).map((label, index) => timeRow(index, label))}</div>
        <div className="p-col-12 p-md-6">{DAYS.slice(4).map((label, i) => timeRow(i + 4, label))}</div>
      </div>

      <h4>Комментарий</h4>
      <InputTextarea
        style={{ width: "100%" }}
        value={terminal.comment}
        onChange={(e) => handleField("comment", e.target.value)}
        rows={1}
        autoResize
        maxLength={70}
      />
    </Dialog>
  );
};

export default DialogEditCarrierTerminal;
